import { useEffect, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  getSuperAdminUsers,
  getDeletedUsers,
  deleteUser,
  bulkDeleteUsers,
  restoreUser,
  SuperAdminUser,
  SuperAdminUsersResponse,
  DeletedUser,
  DeletedUsersResponse
} from 'services/admin/superAdminService';
import Spinner from 'components/common/Spinner';
import { useTheme } from 'contexts/ThemeContext';
import { Users, Search, Trash2, CheckCircle2, AlertTriangle, Inbox, RotateCcw, XCircle, Loader2, Menu } from 'lucide-react';
import { useSidebar } from 'contexts/SidebarContext';
import { logger } from 'utils/logger';
import { getErrorMessage } from 'utils/errors';

type ActiveTab = 'active' | 'deleted';

export default function UserManagement() {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const { toggleSidebar } = useSidebar();

  // Theme tokens
  const containerBg = isDark ? '#1D293D' : '#F8FAFC';
  const mainBg = isDark ? '#0F172B' : '#FFFFFF';
  const mainShadow = isDark ? '0px 1px 2px rgba(2, 6, 24, 0.5)' : '0px 1px 2px rgba(43, 127, 255, 0.1)';
  const headerBorder = isDark ? '#1D293D' : '#F1F5F9';
  const titleColor = isDark ? '#E2E8F0' : '#314158';
  const subtitleColor = isDark ? '#90A1B9' : '#62748E';
  const tableHeaderBg = isDark ? '#1D293D' : '#F8FAFC';
  const tableBorderColor = isDark ? '#1E293B' : '#E2E8F0';
  const tableRowHoverBg = isDark ? '#162030' : '#F8FAFC';
  const inputBg = isDark ? '#1D293D' : '#FFFFFF';
  const inputBorder = isDark ? '#314158' : '#D1D5DB';
  const inputText = isDark ? '#E2E8F0' : '#1E293B';
  const paginationBg = isDark ? '#1D293D' : '#FFFFFF';
  const paginationBorder = isDark ? '#314158' : '#D1D5DB';
  const paginationText = isDark ? '#90A1B9' : '#374151';
  const successBg = isDark ? '#0D2818' : '#F0FDF4';
  const successBorder = isDark ? '#1A4028' : '#BBF7D0';
  const successText = isDark ? '#5EE9B5' : '#166534';
  const errorBg = isDark ? '#2A1215' : '#FEF2F2';
  const errorBorder = isDark ? '#4A1C20' : '#FECACA';
  const errorText = isDark ? '#F87171' : '#991B1B';
  const modalBackdrop = isDark ? 'rgba(0,0,0,0.7)' : 'rgba(0,0,0,0.5)';
  const modalBg = isDark ? '#1D293D' : '#FFFFFF';
  const checkboxAccent = isDark ? '#8B5CF6' : '#7C3AED';

  // State
  const [activeTab, setActiveTab] = useState<ActiveTab>('active');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Active users state
  const [activeUsers, setActiveUsers] = useState<SuperAdminUser[]>([]);
  const [activeUsersPagination, setActiveUsersPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 0
  });

  // Deleted users state
  const [deletedUsers, setDeletedUsers] = useState<DeletedUser[]>([]);
  const [deletedUsersPagination, setDeletedUsersPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 0
  });

  // Search and selection
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);

  // Modal states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState<string | null>(null);
  const [permanentDelete, setPermanentDelete] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Hover state for table rows
  const [hoveredRow, setHoveredRow] = useState<string | null>(null);

  // Fetch active users
  const fetchActiveUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response: SuperAdminUsersResponse = await getSuperAdminUsers({
        page: activeUsersPagination.page,
        limit: activeUsersPagination.limit,
        search: searchTerm || undefined
      });
      setActiveUsers(response.users || []);
      setActiveUsersPagination({
        total: response.pagination?.total || 0,
        page: response.pagination?.page || 1,
        limit: response.pagination?.limit || 10,
        totalPages: response.pagination?.totalPages || 0
      });
    } catch (err: unknown) {
      logger.error('Failed to fetch active users:', err);
      setError(getErrorMessage(err, 'Failed to fetch users'));
      setActiveUsers([]);
    } finally {
      setLoading(false);
    }
  }, [activeUsersPagination.page, activeUsersPagination.limit, searchTerm]);

  // Fetch deleted users
  const fetchDeletedUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response: DeletedUsersResponse = await getDeletedUsers({
        page: deletedUsersPagination.page,
        limit: deletedUsersPagination.limit,
        search: searchTerm || undefined
      });
      setDeletedUsers(response.users || []);
      setDeletedUsersPagination({
        total: response.pagination?.total || 0,
        page: response.pagination?.page || 1,
        limit: response.pagination?.limit || 10,
        totalPages: response.pagination?.totalPages || 0
      });
    } catch (err: unknown) {
      logger.error('Failed to fetch deleted users:', err);
      setError(getErrorMessage(err, 'Failed to fetch deleted users'));
      setDeletedUsers([]);
    } finally {
      setLoading(false);
    }
  }, [deletedUsersPagination.page, deletedUsersPagination.limit, searchTerm]);

  useEffect(() => {
    if (activeTab === 'active') {
      fetchActiveUsers();
    } else {
      fetchDeletedUsers();
    }
  }, [activeTab, fetchActiveUsers, fetchDeletedUsers]);

  // Handle delete user
  const handleDeleteUser = async () => {
    if (!userToDelete) return;

    try {
      setActionLoading(true);
      await deleteUser(userToDelete, permanentDelete);
      setSuccessMessage(permanentDelete ? 'User permanently deleted' : 'User soft deleted successfully');
      setShowDeleteModal(false);
      setUserToDelete(null);
      setPermanentDelete(false);
      fetchActiveUsers();
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Operation failed'));
    } finally {
      setActionLoading(false);
    }
  };

  // Handle bulk delete
  const handleBulkDelete = async () => {
    if (selectedUsers.length === 0) return;

    try {
      setActionLoading(true);
      const response = await bulkDeleteUsers(selectedUsers, permanentDelete);
      setSuccessMessage(`${response.deleted} users deleted successfully`);
      setShowBulkDeleteModal(false);
      setSelectedUsers([]);
      setPermanentDelete(false);
      fetchActiveUsers();
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Operation failed'));
    } finally {
      setActionLoading(false);
    }
  };

  // Handle restore user
  const handleRestoreUser = async (userId: string) => {
    try {
      setActionLoading(true);
      await restoreUser(userId);
      setSuccessMessage('User restored successfully');
      fetchDeletedUsers();
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Operation failed'));
    } finally {
      setActionLoading(false);
    }
  };

  // Handle select all
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedUsers(activeUsers.map(user => user._id));
    } else {
      setSelectedUsers([]);
    }
  };

  // Handle individual select
  const handleSelectUser = (userId: string, checked: boolean) => {
    if (checked) {
      setSelectedUsers([...selectedUsers, userId]);
    } else {
      setSelectedUsers(selectedUsers.filter(id => id !== userId));
    }
  };

  // Format date
  const formatDate = (dateString: string | undefined | null) => {
    if (!dateString) return 'Invalid Date';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid Date';
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Get subscription name (handles both string and object subscription)
  const getSubscriptionName = (subscription: string | { name: string; tier: number } | undefined): string => {
    if (!subscription) return 'Trial';
    if (typeof subscription === 'string') return subscription;
    return subscription.name || 'Trial';
  };

  // Get subscription badge style
  const getSubscriptionBadgeStyle = (subName: string): React.CSSProperties => {
    switch (subName) {
      case 'Elite':
        return { background: isDark ? '#1E1338' : '#F3E8FF', color: isDark ? '#C4B5FD' : '#7C3AED' };
      case 'Pro':
        return { background: isDark ? '#0C1A38' : '#DBEAFE', color: isDark ? '#93C5FD' : '#2563EB' };
      case 'Starter':
        return { background: isDark ? '#0D2818' : '#DCFCE7', color: isDark ? '#5EE9B5' : '#15803D' };
      default:
        return { background: isDark ? '#1D293D' : '#F3F4F6', color: isDark ? '#90A1B9' : '#374151' };
    }
  };

  // Get status badge style
  const getStatusBadgeStyle = (expired: boolean): React.CSSProperties => {
    if (expired) {
      return { background: isDark ? '#2A1215' : '#FEE2E2', color: isDark ? '#F87171' : '#DC2626' };
    }
    return { background: isDark ? '#0D2818' : '#DCFCE7', color: isDark ? '#5EE9B5' : '#15803D' };
  };

  // Clear messages after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Tab button style helper (segmented control)
  const getTabStyle = (tab: ActiveTab): React.CSSProperties => {
    if (activeTab === tab) {
      return {
        padding: '0 16px',
        fontSize: '14px',
        fontWeight: 500,
        border: 'none',
        cursor: 'pointer',
        background: isDark ? '#1C398E' : '#EFF6FF',
        color: isDark ? '#51A2FF' : '#2B7FFF',
        height: '100%',
        display: 'flex',
        alignItems: 'center'
      };
    }
    return {
      padding: '0 16px',
      fontSize: '14px',
      fontWeight: 500,
      border: 'none',
      cursor: 'pointer',
      background: 'transparent',
      color: subtitleColor,
      height: '100%',
      display: 'flex',
      alignItems: 'center'
    };
  };

  // Delete Modal
  const deleteModalJSX = showDeleteModal ? ReactDOM.createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center z-50"
      style={{ background: modalBackdrop, backdropFilter: 'blur(4px)' }}
    >
      <div className="rounded-lg p-6 max-w-md w-full mx-4" style={{ background: modalBg }}>
        <div className="flex items-center gap-3 mb-4">
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center"
            style={{ background: isDark ? '#2A1215' : '#FEE2E2' }}
          >
            <AlertTriangle size={20} style={{ color: isDark ? '#F87171' : '#DC2626' }} />
          </div>
          <h3 className="text-lg font-semibold" style={{ color: titleColor }}>Delete User</h3>
        </div>
        <p className="text-sm mb-4" style={{ color: subtitleColor }}>
          Are you sure you want to delete this user? This action {permanentDelete ? 'cannot be undone' : 'can be reversed by restoring the user'}.
        </p>
        <div className="flex items-center gap-2 mb-4">
          <input
            type="checkbox"
            id="permanentDelete"
            checked={permanentDelete}
            onChange={(e) => setPermanentDelete(e.target.checked)}
            className="rounded"
            style={{ accentColor: '#EF4444', borderColor: inputBorder }}
          />
          <label htmlFor="permanentDelete" className="text-sm" style={{ color: subtitleColor }}>
            Permanently delete (cannot be recovered)
          </label>
        </div>
        <div className="flex justify-end gap-3">
          <button
            onClick={() => {
              setShowDeleteModal(false);
              setUserToDelete(null);
              setPermanentDelete(false);
            }}
            className="px-4 py-2 text-sm font-medium rounded-md transition-colors"
            style={{ color: subtitleColor, background: isDark ? '#111827' : '#F3F4F6' }}
          >
            Cancel
          </button>
          <button
            onClick={handleDeleteUser}
            disabled={actionLoading}
            className="px-4 py-2 text-sm font-medium text-white bg-red-500 rounded-md hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {actionLoading && <Loader2 size={14} className="animate-spin" />}
            Delete
          </button>
        </div>
      </div>
    </div>,
    document.body
  ) : null;

  // Bulk Delete Modal
  const bulkDeleteModalJSX = showBulkDeleteModal ? ReactDOM.createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center z-50"
      style={{ background: modalBackdrop, backdropFilter: 'blur(4px)' }}
    >
      <div className="rounded-lg p-6 max-w-md w-full mx-4" style={{ background: modalBg }}>
        <div className="flex items-center gap-3 mb-4">
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center"
            style={{ background: isDark ? '#2A1215' : '#FEE2E2' }}
          >
            <AlertTriangle size={20} style={{ color: isDark ? '#F87171' : '#DC2626' }} />
          </div>
          <h3 className="text-lg font-semibold" style={{ color: titleColor }}>Bulk Delete Users</h3>
        </div>
        <p className="text-sm mb-4" style={{ color: subtitleColor }}>
          Are you sure you want to delete {selectedUsers.length} users? This action {permanentDelete ? 'cannot be undone' : 'can be reversed by restoring the users'}.
        </p>
        <div className="flex items-center gap-2 mb-4">
          <input
            type="checkbox"
            id="bulkPermanentDelete"
            checked={permanentDelete}
            onChange={(e) => setPermanentDelete(e.target.checked)}
            className="rounded"
            style={{ accentColor: '#EF4444', borderColor: inputBorder }}
          />
          <label htmlFor="bulkPermanentDelete" className="text-sm" style={{ color: subtitleColor }}>
            Permanently delete (cannot be recovered)
          </label>
        </div>
        <div className="flex justify-end gap-3">
          <button
            onClick={() => {
              setShowBulkDeleteModal(false);
              setPermanentDelete(false);
            }}
            className="px-4 py-2 text-sm font-medium rounded-md transition-colors"
            style={{ color: subtitleColor, background: isDark ? '#111827' : '#F3F4F6' }}
          >
            Cancel
          </button>
          <button
            onClick={handleBulkDelete}
            disabled={actionLoading}
            className="px-4 py-2 text-sm font-medium text-white bg-red-500 rounded-md hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {actionLoading && <Loader2 size={14} className="animate-spin" />}
            Delete All
          </button>
        </div>
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <div className="flex flex-col items-start flex-1 min-h-0 font-geist p-0 lg:pt-2 lg:pr-2 lg:pb-2 lg:pl-0"
      style={{ gap: '8px', background: containerBg }}>

      {/* Main Card */}
      <div className="flex flex-col items-start flex-1 min-h-0 w-full overflow-hidden rounded-none lg:rounded-2xl"
        style={{ background: mainBg, boxShadow: mainShadow }}>

        {/* Header */}
        <div className="flex flex-col justify-center items-start w-full flex-shrink-0 rounded-none lg:rounded-t-2xl"
          style={{ padding: '12px 16px', gap: '8px', borderBottom: `1px solid ${headerBorder}` }}>
          <div className="flex flex-row items-center w-full" style={{ gap: '8px' }}>
            {/* Hamburger menu for mobile */}
            <button
              className="lg:hidden flex items-center justify-start flex-shrink-0"
              style={{ width: '24px', height: '36px', background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}
              onClick={toggleSidebar}
            >
              <Menu size={24} color={isDark ? '#E2E8F0' : '#314158'} />
            </button>
            {/* Icon */}
            <div className="hidden lg:flex" style={{ width: '36px', height: '36px', background: isDark ? '#1C398E' : '#EFF6FF', borderRadius: '12px', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Users size={20} style={{ color: isDark ? '#51A2FF' : '#2B7FFF' }} />
            </div>
            {/* Title */}
            <h1 style={{ fontFamily: 'Geist, sans-serif', fontSize: '20px', lineHeight: '28px', fontWeight: 700, color: titleColor, margin: 0, flex: 1 }}>
              {t('superAdmin.userManagement.title', 'User Management')}
            </h1>
          </div>
          {/* Tab Navigation - SEPARATE ROW */}
          <div className="w-full overflow-x-auto">
            <div style={{ display: 'flex', border: `1px solid ${headerBorder}`, borderRadius: '8px', overflow: 'hidden', height: '36px', width: 'fit-content' }}>
              <button
                onClick={() => setActiveTab('active')}
                style={getTabStyle('active')}
              >
                {t('superAdmin.tabs.activeUsers', 'Active Users')}
              </button>
              <button
                onClick={() => setActiveTab('deleted')}
                style={getTabStyle('deleted')}
              >
                {t('superAdmin.tabs.deletedUsers', 'Deleted Users')}
              </button>
            </div>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className={`overflow-y-auto ${isDark ? 'scrollbar-themed-dark' : 'scrollbar-themed-light'}`} style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '16px', width: '100%', flex: 1, minHeight: 0 }}>

          {/* Search and Actions Bar - no card wrapper */}
          <div className="flex flex-col sm:flex-row gap-3 justify-between">
            <div className="flex-1 max-w-md">
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: subtitleColor }} />
                <input
                  type="text"
                  placeholder={t('superAdmin.search.placeholder', 'Search users by name or email...')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  style={{ background: inputBg, borderWidth: 1, borderStyle: 'solid', borderColor: inputBorder, color: inputText }}
                />
              </div>
            </div>
            {activeTab === 'active' && selectedUsers.length > 0 && (
              <button
                onClick={() => setShowBulkDeleteModal(true)}
                className="px-4 py-2 bg-red-500 text-white text-sm font-medium rounded-md hover:bg-red-600 transition-colors flex items-center gap-2"
              >
                <Trash2 size={14} />
                Delete Selected ({selectedUsers.length})
              </button>
            )}
          </div>

          {/* Messages */}
          {successMessage && (
            <div
              className="p-3 rounded-lg text-sm flex items-center gap-2"
              style={{ background: successBg, borderWidth: 1, borderStyle: 'solid', borderColor: successBorder, color: successText }}
            >
              <CheckCircle2 size={16} />
              {successMessage}
            </div>
          )}
          {error && (
            <div
              className="p-3 rounded-lg text-sm flex items-center gap-2"
              style={{ background: errorBg, borderWidth: 1, borderStyle: 'solid', borderColor: errorBorder, color: errorText }}
            >
              <AlertTriangle size={16} />
              {error}
            </div>
          )}

          {/* Loading State */}
          {loading && <Spinner size="large" text={t('superAdmin.loading', 'Loading users...')} fullPage />}

          {/* Active Users Table */}
          {!loading && activeTab === 'active' && (
            <div className={isDark ? 'scrollbar-themed-dark' : 'scrollbar-themed-light'} style={{ border: `1px solid ${tableBorderColor}`, borderRadius: '8px', overflow: 'auto' }}>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead style={{ background: tableHeaderBg, borderBottom: `1px solid ${tableBorderColor}` }}>
                    <tr>
                      <th className="px-4 py-3 text-left">
                        <input
                          type="checkbox"
                          checked={selectedUsers.length === activeUsers.length && activeUsers.length > 0}
                          onChange={(e) => handleSelectAll(e.target.checked)}
                          className="rounded"
                          style={{ accentColor: checkboxAccent, borderColor: inputBorder }}
                        />
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: subtitleColor }}>User</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: subtitleColor }}>Email</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: subtitleColor }}>Subscription</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: subtitleColor }}>Accounts</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: subtitleColor }}>Status</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: subtitleColor }}>Joined</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: subtitleColor }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeUsers.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="px-4 py-8 text-center" style={{ color: subtitleColor }}>
                          <Inbox size={30} className="mx-auto mb-2" />
                          No users found
                        </td>
                      </tr>
                    ) : (
                      activeUsers.map((user) => (
                        <tr
                          key={user._id}
                          onMouseEnter={() => setHoveredRow(user._id)}
                          onMouseLeave={() => setHoveredRow(null)}
                          style={{
                            background: hoveredRow === user._id ? tableRowHoverBg : 'transparent',
                            borderBottom: `1px solid ${tableBorderColor}`
                          }}
                        >
                          <td className="px-4 py-3">
                            <input
                              type="checkbox"
                              checked={selectedUsers.includes(user._id)}
                              onChange={(e) => handleSelectUser(user._id, e.target.checked)}
                              className="rounded"
                              style={{ accentColor: checkboxAccent, borderColor: inputBorder }}
                            />
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              {user.profile_picture ? (
                                <img src={user.profile_picture} alt={user.fullName} className="w-8 h-8 rounded-full object-cover" />
                              ) : (
                                <div
                                  className="w-8 h-8 rounded-full flex items-center justify-center"
                                  style={{ background: isDark ? '#1E1338' : '#F3E8FF' }}
                                >
                                  <span className="text-sm font-medium" style={{ color: isDark ? '#C4B5FD' : '#7C3AED' }}>
                                    {user.fullName?.charAt(0)?.toUpperCase() || '?'}
                                  </span>
                                </div>
                              )}
                              <span className="text-sm font-medium" style={{ color: titleColor }}>{user.fullName || 'Unknown'}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm" style={{ color: subtitleColor }}>{user.email}</td>
                          <td className="px-4 py-3">
                            {(() => {
                              const subName = getSubscriptionName(user.subscription_name);
                              return (
                                <span
                                  className="px-2 py-1 text-xs font-medium rounded-full"
                                  style={getSubscriptionBadgeStyle(subName)}
                                >
                                  {subName}
                                </span>
                              );
                            })()}
                          </td>
                          <td className="px-4 py-3 text-sm" style={{ color: subtitleColor }}>
                            <span style={{ color: isDark ? '#5EE9B5' : '#16A34A' }}>{user.activeAccountsCount || 0}</span>
                            <span style={{ color: isDark ? '#475569' : '#9CA3AF' }}> / </span>
                            <span>{user.accountsCount || 0}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className="px-2 py-1 text-xs font-medium rounded-full"
                              style={getStatusBadgeStyle(user.expired)}
                            >
                              {user.expired ? 'Expired' : 'Active'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm" style={{ color: subtitleColor }}>{formatDate(user.joiningDate || user.created_on)}</td>
                          <td className="px-4 py-3">
                            <button
                              onClick={() => {
                                setUserToDelete(user._id);
                                setShowDeleteModal(true);
                              }}
                              className="text-red-500 hover:text-red-700 transition-colors"
                              title="Delete User"
                            >
                              <Trash2 size={14} />
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="px-4 py-3 flex items-center justify-between flex-wrap gap-2" style={{ borderTop: `1px solid ${tableBorderColor}` }}>
                <div className="text-sm" style={{ color: subtitleColor }}>
                  Showing {((activeUsersPagination.page - 1) * activeUsersPagination.limit) + 1} to {Math.min(activeUsersPagination.page * activeUsersPagination.limit, activeUsersPagination.total)} of {activeUsersPagination.total} users
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setActiveUsersPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                    disabled={activeUsersPagination.page === 1}
                    className="px-3 py-1 text-sm rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ background: paginationBg, borderWidth: 1, borderStyle: 'solid', borderColor: paginationBorder, color: paginationText }}
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setActiveUsersPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                    disabled={activeUsersPagination.page >= activeUsersPagination.totalPages}
                    className="px-3 py-1 text-sm rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ background: paginationBg, borderWidth: 1, borderStyle: 'solid', borderColor: paginationBorder, color: paginationText }}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Deleted Users Table */}
          {!loading && activeTab === 'deleted' && (
            <div className={isDark ? 'scrollbar-themed-dark' : 'scrollbar-themed-light'} style={{ border: `1px solid ${tableBorderColor}`, borderRadius: '8px', overflow: 'auto' }}>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead style={{ background: tableHeaderBg, borderBottom: `1px solid ${tableBorderColor}` }}>
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: subtitleColor }}>User</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: subtitleColor }}>Email</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: subtitleColor }}>Role</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: subtitleColor }}>Deleted At</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: subtitleColor }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deletedUsers.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-4 py-8 text-center" style={{ color: subtitleColor }}>
                          <Inbox size={30} className="mx-auto mb-2" />
                          No deleted users found
                        </td>
                      </tr>
                    ) : (
                      deletedUsers.map((user) => (
                        <tr
                          key={user._id}
                          onMouseEnter={() => setHoveredRow(user._id)}
                          onMouseLeave={() => setHoveredRow(null)}
                          style={{
                            background: hoveredRow === user._id ? tableRowHoverBg : 'transparent',
                            borderBottom: `1px solid ${tableBorderColor}`
                          }}
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              {user.profilePicture ? (
                                <img src={user.profilePicture} alt={user.name} className="w-8 h-8 rounded-full object-cover opacity-50" />
                              ) : (
                                <div
                                  className="w-8 h-8 rounded-full flex items-center justify-center"
                                  style={{ background: isDark ? '#1D293D' : '#F3F4F6' }}
                                >
                                  <span className="text-sm font-medium" style={{ color: isDark ? '#64748B' : '#9CA3AF' }}>
                                    {user.name?.charAt(0)?.toUpperCase() || '?'}
                                  </span>
                                </div>
                              )}
                              <span className="text-sm font-medium" style={{ color: subtitleColor }}>{user.name}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm" style={{ color: subtitleColor }}>{user.email}</td>
                          <td className="px-4 py-3">
                            <span
                              className="px-2 py-1 text-xs font-medium rounded-full"
                              style={{ background: isDark ? '#1D293D' : '#F3F4F6', color: isDark ? '#90A1B9' : '#6B7280' }}
                            >
                              {user.role}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm" style={{ color: subtitleColor }}>{formatDate(user.deletedAt)}</td>
                          <td className="px-4 py-3">
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleRestoreUser(user._id)}
                                disabled={actionLoading}
                                className="text-green-500 hover:text-green-700 transition-colors disabled:opacity-50"
                                title="Restore User"
                              >
                                <RotateCcw size={14} />
                              </button>
                              <button
                                onClick={() => {
                                  setUserToDelete(user._id);
                                  setPermanentDelete(true);
                                  setShowDeleteModal(true);
                                }}
                                className="text-red-500 hover:text-red-700 transition-colors"
                                title="Permanently Delete"
                              >
                                <XCircle size={14} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {deletedUsers.length > 0 && (
                <div className="px-4 py-3 flex items-center justify-between flex-wrap gap-2" style={{ borderTop: `1px solid ${tableBorderColor}` }}>
                  <div className="text-sm" style={{ color: subtitleColor }}>
                    Showing {((deletedUsersPagination.page - 1) * deletedUsersPagination.limit) + 1} to {Math.min(deletedUsersPagination.page * deletedUsersPagination.limit, deletedUsersPagination.total)} of {deletedUsersPagination.total} users
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setDeletedUsersPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                      disabled={deletedUsersPagination.page === 1}
                      className="px-3 py-1 text-sm rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ background: paginationBg, borderWidth: 1, borderStyle: 'solid', borderColor: paginationBorder, color: paginationText }}
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setDeletedUsersPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                      disabled={deletedUsersPagination.page >= deletedUsersPagination.totalPages}
                      className="px-3 py-1 text-sm rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ background: paginationBg, borderWidth: 1, borderStyle: 'solid', borderColor: paginationBorder, color: paginationText }}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modal Portals */}
      {deleteModalJSX}
      {bulkDeleteModalJSX}
    </div>
  );
}
